/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiTranslatorProfilesPage.tsx
 🧩 Description:
    AI Translator Profiles list page — paginated grid with
    search, sort, and CRUD actions.

 🔁 Last Updated: 2026-05-12 by Rodrigo Cuadra (Standardized)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Languages, Edit, Trash2, Copy } from 'lucide-react';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDataGrid, type DataGridFetchResult } from '../../hooks/useDataGrid';
import { ModulePageContainer } from '../../components/layouts';
import { aiTranslatorProfilesApi } from './api/aiTranslatorProfilesApi';
import { useI18n } from '../../hooks/useI18n';
import { useAiTranslatorProfilesColumns } from './hooks/useAiTranslatorProfilesColumns';
import type { AiTranslatorProfile } from './api/types';

export default function AiTranslatorProfilesPage() {
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();
    const { t } = useI18n();

    const { availableColumns } = useAiTranslatorProfilesColumns();
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; profile: AiTranslatorProfile | null }>({
        isOpen: false,
        profile: null
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);

    const fetchData = useCallback(async (params: {
        page: number;
        limit: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        search?: string;
    }): Promise<DataGridFetchResult> => {
        const res = await aiTranslatorProfilesApi.getProfiles({
            page: params.page,
            limit: params.limit,
            search: params.search
        });
        return {
            data: res.data,
            pagination: {
                page:       res.meta.page       ?? params.page,
                limit:      res.meta.limit      ?? params.limit,
                total:      res.meta.total      ?? res.data.length,
                totalPages: res.meta.totalPages ?? Math.ceil((res.meta.total ?? res.data.length) / params.limit)
            }
        };
    }, []);

    const {
        data, loading, columns, pagination, searchQuery,
        sortBy, sortOrder, handleSearch, handleSort,
        handlePageChange, handleItemsPerPageChange, handleColumnToggle, refresh
    } = useDataGrid({
        fetchData,
        initialColumns: availableColumns,
        initialSortBy: 'name',
        initialSortOrder: 'asc',
        initialLimit: 10,
        moduleName: 'aiTranslatorProfiles'
    });

    const handleCreate = useCallback(() => {
        navigate('/admin/ai-integration/translator-profiles/new');
    }, [navigate]);

    const handleEdit = useCallback((p: AiTranslatorProfile) => {
        if (!p?.uuid) {
            showError('Cannot open profile: missing UUID.');
            return;
        }
        navigate(`/admin/ai-integration/translator-profiles/${p.uuid}`);
    }, [navigate, showError]);


    const handleDeleteClick = useCallback((p: AiTranslatorProfile) => {
        setDeleteModal({ isOpen: true, profile: p });
    }, []);

    const handleDuplicate = useCallback(async (p: AiTranslatorProfile) => {
        if (isDuplicating) return;
        setIsDuplicating(true);
        try {
            await aiTranslatorProfilesApi.duplicateProfile(p.uuid || p.id);
            showSuccess(t('common.duplicatedSuccessfully') || 'Profile duplicated successfully');
            refresh();
        } catch {
            showError(t('common.errorDuplicating') || 'Failed to duplicate profile');
        } finally {
            setIsDuplicating(false);
        }
    }, [isDuplicating, refresh, showSuccess, showError, t]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteModal.profile) return;
        setIsDeleting(true);
        try {
            await aiTranslatorProfilesApi.deleteProfile(deleteModal.profile.id);
            setDeleteModal({ isOpen: false, profile: null });
            showSuccess(t('common.deletedSuccessfully'));
            refresh();
        } catch {
            showError(t('common.errorDeleting'));
        } finally {
            setIsDeleting(false);
        }
    }, [deleteModal.profile, refresh, showSuccess, showError, t]);

    const renderRowActions = useMemo(() => (item: AiTranslatorProfile) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('common.duplicate') || 'Duplicate'} position="top">
                <button
                    type="button"
                    onClick={() => handleDuplicate(item)}
                    disabled={isDuplicating}
                    className="p-2 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60 disabled:opacity-50"
                >
                    <Copy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    type="button"
                    onClick={() => handleDeleteClick(item)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [handleEdit, handleDuplicate, handleDeleteClick, isDuplicating, t]);

    return (
        <ModulePageContainer
            currentView="list"
            listView={
                <DataGridSimple
                    data={data}
                    columns={columns}
                    onColumnToggle={handleColumnToggle}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    searchQuery={searchQuery}
                    onSearch={handleSearch}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    onAdd={handleCreate}
                    renderActions={renderRowActions}
                    title="Translator Profiles"
                    titleIcon={<Languages className="h-5 w-5 text-primary" />}
                    addButtonText={t('common.add')}
                    searchPlaceholder="Search profiles..."
                    emptyMessage={t('common.noItemsFound')}
                    translations={{
                        items: t('common.items'),
                        columns: t('common.columns'),
                        noItemsFound: t('common.noItemsFound'),
                        showing: t('common.showing'),
                        to: t('common.to'),
                        of: t('common.of'),
                        results: t('common.results'),
                        previous: t('common.previous'),
                        next: t('common.next'),
                        page: t('common.page'),
                        deleteItem: t('common.deleteItem'),
                        areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
                        delete: t('common.delete'),
                        cancel: t('common.cancel'),
                        allSelected: t('common.allSelected'),
                        selected: t('common.selected'),
                        searchOptions: t('common.searchOptions'),
                        noOptionsFound: t('common.noOptionsFound'),
                        selectAll: t('common.selectAll'),
                        clearAll: t('common.clearAll')
                    }}
                />
            }
            formView={null}
        >
            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, profile: null })}
                onConfirm={handleDeleteConfirm}
                title={t('common.deleteItem')}
                message={
                    deleteModal.profile
                        ? `${t('common.areYouSureDeleteItem') || 'Are you sure you want to delete'} "${deleteModal.profile.name}"?`
                        : ''
                }
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                isLoading={isDeleting}
                type="danger"
            />
        </ModulePageContainer>
    );
}
